import { useEffect, useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { useDirtyTracking } from "../../settings/defaults/useDirtyTracking";
import type { BlogStatus } from "../blog-list/StatusPill";
import { BlogBodyEditor } from "./BlogBodyEditor";
import { DetailsPanel } from "./DetailsPanel";
import { EditorTopBar } from "./EditorTopBar";
import { StatusPanel, type PublishMode } from "./StatusPanel";
import { TagInput } from "./TagInput";
import { ImageUploadField } from "../page-canvas/inspector/ImageUploadField";
import { getCropConstraint } from "../page-canvas/inspector/cropConstraints";

export interface BlogPostFormValues {
  title: string;
  slug: string;
  author: string;
  summary: string;
  body: string;
  featuredImageUrl: string;
  featuredImageAlt: string;
  tagNames: string[];
  publishMode: PublishMode;
  /** ISO string. Used by both "schedule" and "now" — for "now" it's the
   *  back-date the user picked (or the existing publishAt of a live post).
   *  Null + mode "now" means "use current time at save". */
  scheduledAt: string | null;
}

export interface BlogPostSavePayload extends BlogPostFormValues {
  /** Resolved publishAt to send to the API (null = draft). */
  publishAt: string | null;
}

interface Props {
  mode: "create" | "edit";
  initialValues: BlogPostFormValues;
  /** publishAt as currently saved. Used to keep already-published posts
   *  from getting their publication date bumped on every save. */
  originalPublishAt: string | null;
  isSaving: boolean;
  saveError?: string | null;
  /** Blog base path from tenant featureConfig (e.g. "/legal-insights"). */
  blogBasePath?: string;
  /** Resolved when the parent has committed the change (e.g. for "create"
   *  redirects). */
  onSave: (payload: BlogPostSavePayload) => Promise<void>;
  onDelete?: () => void;
  isDeleting?: boolean;
}

const EMPTY_TAGS: string[] = [];

/**
 * Two-column blog post editor. Title + body live in the main column;
 * status, details, featured image, and tags stack in the right rail.
 * The component is purely presentational with respect to data — the
 * Create/Edit wrappers own the GraphQL mutations and pass `onSave`.
 */
export function BlogPostEditor({
  mode,
  initialValues,
  originalPublishAt,
  isSaving,
  saveError,
  blogBasePath,
  onSave,
  onDelete,
  isDeleting,
}: Props) {
  const tracking = useDirtyTracking<BlogPostFormValues>(initialValues);

  // Re-hydrate the form whenever the parent passes new initial values
  // (e.g. after a successful save bumps `updatedAt`). markSaved keeps
  // the dirty count at 0 after hydration.
  const [hydratedKey, setHydratedKey] = useState<string>(
    JSON.stringify(initialValues),
  );
  useEffect(() => {
    const next = JSON.stringify(initialValues);
    if (next !== hydratedKey) {
      tracking.markSaved(initialValues);
      setHydratedKey(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  // Slug auto-generation: only on new posts, and only until the user
  // touches the slug field directly.
  const [autoSlug, setAutoSlug] = useState(mode === "create");
  useEffect(() => {
    if (!autoSlug) return;
    const next = slugifyTitle(tracking.values.title);
    if (next !== tracking.values.slug) tracking.setField("slug", next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracking.values.title, autoSlug]);

  const v = tracking.values;
  const setField = tracking.setField;
  const derivedStatus = deriveStatus(v);
  const slugError = !v.slug.trim() && tracking.isDirty ? "Slug is required" : null;

  const canSave = useMemo(() => {
    if (isSaving || isDeleting) return false;
    if (!v.title.trim()) return false;
    if (!v.slug.trim()) return false;
    if (!v.author.trim()) return false;
    if (v.publishMode === "schedule" && !v.scheduledAt) return false;
    if (mode === "edit" && !tracking.isDirty) return false;
    return true;
  }, [
    isSaving,
    isDeleting,
    v.title,
    v.slug,
    v.author,
    v.publishMode,
    v.scheduledAt,
    mode,
    tracking.isDirty,
  ]);

  const saveLabel = useMemo(() => {
    if (v.publishMode === "draft") return "Save draft";
    if (v.publishMode === "schedule") return "Schedule";
    // "now" mode: if the user picked a future date, the save acts like a schedule
    if (
      v.scheduledAt &&
      new Date(v.scheduledAt).getTime() > Date.now()
    ) {
      return "Schedule";
    }
    if (mode === "edit") return "Save changes";
    return "Publish";
  }, [v.publishMode, v.scheduledAt, mode]);

  async function handleSave() {
    const payload: BlogPostSavePayload = {
      ...v,
      publishAt: resolvePublishAt(v, originalPublishAt),
    };
    await onSave(payload);
  }

  return (
    <div className="min-h-full bg-paper">
      <EditorTopBar
        title={v.title}
        status={derivedStatus}
        isDirty={tracking.isDirty}
        isSaving={isSaving}
        saveLabel={saveLabel}
        canSave={canSave}
        onDiscard={mode === "edit" ? tracking.reset : undefined}
        onSave={handleSave}
      />

      <div className="grid min-h-[calc(100vh-3.5rem-var(--banner-h))] grid-cols-[1fr_360px] gap-0">
        {/* Main column — title + body */}
        <div className="flex min-w-0 flex-col">
          <div className="px-8 pb-6 pt-8">
            <input
              type="text"
              value={v.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Untitled post"
              className="font-display w-full bg-transparent text-[36px] font-medium leading-[1.1] tracking-tight text-ink placeholder:text-ink-5 focus:outline-none"
            />
            {saveError && (
              <div className="mt-3 rounded-md bg-rust-s/40 px-3 py-2 text-[12.5px] text-rust">
                {saveError}
              </div>
            )}
          </div>
          <div className="flex-1 px-8 pb-12">
            <div className="shadow-card h-full overflow-hidden rounded-lg bg-paper-hi">
              <BlogBodyEditor
                value={v.body}
                onChange={(next) => setField("body", next)}
              />
            </div>
          </div>
        </div>

        {/* Right rail — status + details + featured + tags */}
        <aside className="hair-l border-l overflow-y-auto bg-canvas/30 px-4 py-6">
          <div className="space-y-3">
            <StatusPanel
              status={derivedStatus}
              mode={v.publishMode}
              scheduledAt={v.scheduledAt}
              onModeChange={(next) => {
                tracking.setValues({ ...v, publishMode: next });
              }}
              onScheduledAtChange={(next) => setField("scheduledAt", next)}
            />
            <DetailsPanel
              slug={v.slug}
              author={v.author}
              summary={v.summary}
              slugError={slugError}
              blogBasePath={blogBasePath}
              onSlugChange={(next) => {
                if (autoSlug) setAutoSlug(false);
                setField("slug", next);
              }}
              onAuthorChange={(next) => setField("author", next)}
              onSummaryChange={(next) => setField("summary", next)}
            />
            <div className="shadow-card rounded-lg bg-paper-hi">
              <div className="hair-b border-b px-4 py-3">
                <h3 className="text-[13px] font-semibold">Featured image</h3>
              </div>
              <div className="p-4">
                <ImageUploadField
                  label=""
                  value={v.featuredImageUrl || undefined}
                  onChange={(url) => setField("featuredImageUrl", url)}
                  cropConstraint={getCropConstraint("BlogFeaturedImage", "featuredImageUrl")}
                />
                {v.featuredImageUrl && (
                  <div className="mt-2">
                    <label
                      htmlFor="featured-image-alt"
                      className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-ink-4"
                    >
                      Alt text
                    </label>
                    <textarea
                      id="featured-image-alt"
                      value={v.featuredImageAlt}
                      onChange={(e) => setField("featuredImageAlt", e.target.value)}
                      rows={2}
                      maxLength={255}
                      placeholder="Describe the image..."
                      className="hair w-full rounded-md border bg-paper px-2.5 py-1.5 text-[12.5px] leading-relaxed text-ink-2 focus:border-tide focus:outline-none"
                    />
                    <p className="mt-1 text-[11px] text-ink-5">
                      Read by screen readers. Leave blank if the image is purely
                      decorative.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <TagInput
              tags={v.tagNames ?? EMPTY_TAGS}
              onChange={(next) => setField("tagNames", next)}
            />

            {mode === "edit" && onDelete && (
              <div className="hair rounded-lg border border-rust/30 bg-paper-hi p-4">
                <div className="text-[12.5px] font-medium text-ink-2">
                  Delete this post
                </div>
                <p className="mt-1 text-[11.5px] text-ink-4">
                  Removes the post and any uploaded images. Cannot be undone.
                </p>
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={isDeleting || isSaving}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-rust/30 px-3 py-1.5 text-[12px] font-medium text-rust hover:bg-rust-s/40 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2 size={12} strokeWidth={2} />
                  {isDeleting ? "Deleting…" : "Delete post"}
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

export function emptyBlogPostFormValues(): BlogPostFormValues {
  return {
    title: "",
    slug: "",
    author: "",
    summary: "",
    body: "",
    featuredImageUrl: "",
    featuredImageAlt: "",
    tagNames: [],
    publishMode: "draft",
    scheduledAt: null,
  };
}

export function blogPostFormValuesFromRecord(record: {
  title: string;
  slug: string;
  author: string;
  summary: string | null;
  body: string | null;
  featuredImageUrl: string | null;
  featuredImageAlt: string | null;
  tagNames: string[];
  publishAt: string | null;
}): BlogPostFormValues {
  const { mode, scheduledAt } = derivePublishMode(record.publishAt);
  return {
    title: record.title,
    slug: record.slug,
    author: record.author,
    summary: record.summary ?? "",
    body: record.body ?? "",
    featuredImageUrl: record.featuredImageUrl ?? "",
    featuredImageAlt: record.featuredImageAlt ?? "",
    tagNames: record.tagNames ?? [],
    publishMode: mode,
    scheduledAt,
  };
}

function deriveStatus(v: BlogPostFormValues): BlogStatus {
  if (v.publishMode === "draft") return "draft";
  if (v.publishMode === "schedule") {
    if (!v.scheduledAt) return "draft";
    return new Date(v.scheduledAt).getTime() <= Date.now()
      ? "published"
      : "scheduled";
  }
  return "published";
}

function derivePublishMode(publishAt: string | null): {
  mode: PublishMode;
  scheduledAt: string | null;
} {
  if (!publishAt) return { mode: "draft", scheduledAt: null };
  const t = new Date(publishAt).getTime();
  if (Number.isNaN(t)) return { mode: "draft", scheduledAt: null };
  if (t > Date.now()) return { mode: "schedule", scheduledAt: publishAt };
  // Already-published post: keep mode "now" but seed the picker with the
  // existing publish date so the user can see/edit it.
  return { mode: "now", scheduledAt: publishAt };
}

function resolvePublishAt(
  v: BlogPostFormValues,
  _originalPublishAt: string | null,
): string | null {
  if (v.publishMode === "draft") return null;
  if (v.publishMode === "schedule") return v.scheduledAt;
  // mode === "now": use whatever the user picked, or current time if blank.
  if (v.scheduledAt) return v.scheduledAt;
  return new Date().toISOString();
}

function slugifyTitle(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}
